import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { QueryGroupsFilters } from "../types";

export type SortKey =
  | "count"
  | "avg_execution_time"
  | "avg_cost"
  | "total_cost"
  | "annualized_cost";

interface UseGroupsFiltersProps {
  queryHash?: string;
}

export function useGroupsFilters({ queryHash }: UseGroupsFiltersProps) {
  const { getFilterValue, isInitialized } = useDatatableContext();
  const { page, pageSize } = useTablePagination();
  const [searchParams] = useSearchParams();

  // Get sort values from URL params
  const sortAttribute = (searchParams.get("sortKey") as SortKey) || "annualized_cost";
  const sortOrder = searchParams.get("sortOrder") || "desc";

  // Get date filters from context
  const startDate = useMemo(
    () => getFilterValue<Date>("start_date") || dayjs().subtract(28, "day").toDate(),
    [getFilterValue]
  );
  const endDate = useMemo(
    () => getFilterValue<Date>("end_date") || dayjs().toDate(),
    [getFilterValue]
  );

  // Filter getters from context
  const tagFilter = useMemo(() => getFilterValue<string[]>("tags") || [], [getFilterValue]);
  const warehouseFilter = useMemo(
    () => getFilterValue<string[]>("warehouses") || [],
    [getFilterValue]
  );
  const userFilter = useMemo(() => getFilterValue<string[]>("users") || [], [getFilterValue]);
  const instanceFilter = useMemo(
    () => getFilterValue<string[]>("instance") || [],
    [getFilterValue]
  );

  // Convert filters to API parameters
  const filterParams = useMemo((): QueryGroupsFilters => {
    const params: QueryGroupsFilters = {
      page: page + 1,
      size: pageSize,
      sort_key: sortAttribute,
      sort_order: sortOrder,
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate)!.toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)!.toISOString(),
    };

    if (queryHash) params.query_hash = queryHash;
    if (tagFilter.length > 0) params.tags = tagFilter;
    if (userFilter.length > 0) params.users = userFilter;
    if (warehouseFilter.length > 0) params.warehouses = warehouseFilter;
    if (instanceFilter.length > 0) params.instance_ids = instanceFilter;

    return params;
  }, [
    page,
    pageSize,
    sortAttribute,
    sortOrder,
    startDate,
    endDate,
    queryHash,
    tagFilter,
    userFilter,
    warehouseFilter,
    instanceFilter,
  ]);

  return {
    filterParams,
    isInitialized,
    // Export individual filter values for use in components
    tagFilter,
    warehouseFilter,
    userFilter,
    instanceFilter,
    startDate,
    endDate,
  };
}
